import moment from 'moment'
import { useState, useEffect } from 'react'
import { AddToCalendarButton } from 'add-to-calendar-button-react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Button } from '@/components/button'
import { Group, Label } from '@/components/form'
import { createModal, useModalize } from '@/components/modalize/mui'
import useRole from '@/hooks/use-role'
import { fontSizes, fontWeights, colors } from '@/styles'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 95vh;
  margin: auto;
  padding: 0 16px;
  min-height: 440px;
`

const Title = styled.h4`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.medium};
  text-align: center;
  width: 100%;
  padding: 60px 0 20px;
`

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0 10px 20px 22px;
`

const AddToCalendarWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  position: relative;
  margin: auto;
  .chq-atc {
    margin-bottom: 60px;
  }
  .chq-atc--button {
    cursor: pointer;
    width: 250px;
    display: block;
    text-align: center;
    transition: opacity 0.2s ease-in-out;
    border: 1.5px solid ${colors.grey88};
    padding: 16px;
    background-color: ${colors.white};
    color: ${colors.blue16};
    font-weight: 600;
    :hover {
      background-color: ${colors.white};
      opacity: 0.7;
    }
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 10px 0 30px;
`

const AddToCalendarModal = ({ title = 'Add To Calendar', values, listingId }) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const dispatch = useDispatch()
  const [data, setData] = useState(null)
  const role = useRole()
  useEffect(() => {
    const loadData = async () => {
      const { body } = await dispatch.listing.getListingSummary(listingId)
      setData(body)
    }
    loadData()
  }, [dispatch.listing, listingId])

  const unitHidden = (!!Number(data?.hide_unit_number) || !data?.unit_number) && role === 'tenant'
  const isApartment = data?.building_type !== 'house'
  const location = `${!unitHidden ? data?.unit_number + ' - ' : ''}${
    !unitHidden || isApartment ? data?.full_street_name : data?.street_name
  }, ${data?.city}, ${data?.state}`

  return (
    <Wrapper>
      <Title>{title}</Title>
      <LabelWrapper>
        <Label>
          {t(
            'l.listing.showings.add_to_calendar.label',
            'Add this appointment to your preferred calendar.'
          )}
        </Label>
      </LabelWrapper>
      <Group bottom={20}>
        <AddToCalendarWrapper>
          <AddToCalendarButton
            name="Showing Appointment"
            location={location}
            startDate={moment(values.meeting_at).format('YYYY-MM-DD')}
            startTime={moment(values.meeting_at).format('HH:mm')}
            endDate={moment(values.meeting_at).add(values.duration, 'minutes').format('YYYY-MM-DD')}
            endTime={moment(values.meeting_at).add(values.duration, 'minutes').format('HH:mm')}
            timeZone="currentBrowser"
          />
        </AddToCalendarWrapper>
      </Group>
      <ButtonWrapper>
        <Button theme="primary" onClick={close}>
          {t('common.done', 'Done')}
        </Button>
      </ButtonWrapper>
    </Wrapper>
  )
}

export const showAddToCalendarModal = createModal(AddToCalendarModal)
